import { Controller } from "@hotwired/stimulus"

/**
 * Confirm Controller — Branded confirmation dialog
 *
 * Replaces the browser's native confirm() for Turbo and custom triggers.
 * Uses the CSS classes defined in application.tailwind.css (.confirm-*)
 *
 * Usage (standalone button):
 *   <button data-controller="confirm"
 *           data-confirm-message-value="Delete this project?"
 *           data-confirm-title-value="Confirm deletion"
 *           data-confirm-variant-value="danger"
 *           data-action="confirm#show">
 *     Delete
 *   </button>
 *
 * For Turbo form submissions, this also integrates via Turbo.setConfirmMethod.
 */
export default class extends Controller {
  static values = {
    title: { type: String, default: "Are you sure?" },
    message: { type: String, default: "This action cannot be undone." },
    confirmLabel: { type: String, default: "Confirm" },
    cancelLabel: { type: String, default: "Cancel" },
    variant: { type: String, default: "default" } // "default" | "danger"
  }

  show(event) {
    event.preventDefault()
    event.stopImmediatePropagation()

    this.createDialog().then((confirmed) => {
      if (confirmed) {
        // Re-dispatch the original event without the confirm controller blocking
        this.element.removeAttribute("data-action")
        this.element.click()
      }
    })
  }

  createDialog() {
    return new Promise((resolve) => {
      const overlay = document.createElement("div")
      overlay.className = "confirm-overlay"
      overlay.innerHTML = `
        <div class="confirm-panel">
          <div class="confirm-body">
            <p class="confirm-title">${this.escapeHtml(this.titleValue)}</p>
            <p class="confirm-message">${this.escapeHtml(this.messageValue)}</p>
          </div>
          <div class="confirm-actions">
            <button class="confirm-cancel" data-role="cancel">${this.escapeHtml(this.cancelLabelValue)}</button>
            <button class="confirm-ok ${this.variantValue === "danger" ? "confirm-ok--danger" : ""}" data-role="confirm">${this.escapeHtml(this.confirmLabelValue)}</button>
          </div>
        </div>
      `

      const cleanup = (result) => {
        overlay.remove()
        document.removeEventListener("keydown", keyHandler)
        resolve(result)
      }

      const keyHandler = (e) => {
        if (e.key === "Escape") cleanup(false)
        if (e.key === "Enter") cleanup(true)
      }

      overlay.querySelector('[data-role="cancel"]').addEventListener("click", () => cleanup(false))
      overlay.querySelector('[data-role="confirm"]').addEventListener("click", () => cleanup(true))
      overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(false) })
      document.addEventListener("keydown", keyHandler)

      document.body.appendChild(overlay)
      overlay.querySelector('[data-role="confirm"]').focus()
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

/**
 * Override Turbo's built-in confirm method to use our branded dialog
 * instead of the browser's native confirm() dialog.
 */
import * as Turbo from "@hotwired/turbo"

Turbo.config.forms.confirm = (message, element) => {
  return new Promise((resolve) => {
    // Detect if this is a destructive action
    const method = element?.getAttribute("data-turbo-method")
    const isDanger = method === "delete" || message.toLowerCase().includes("delete")
    const variant = isDanger ? "danger" : "default"

    const overlay = document.createElement("div")
    overlay.className = "confirm-overlay"
    overlay.innerHTML = `
      <div class="confirm-panel">
        <div class="confirm-body">
          <p class="confirm-title">${isDanger ? "Confirm deletion" : "Are you sure?"}</p>
          <p class="confirm-message">${escapeHtmlGlobal(message)}</p>
        </div>
        <div class="confirm-actions">
          <button class="confirm-cancel" data-role="cancel">Cancel</button>
          <button class="confirm-ok ${variant === "danger" ? "confirm-ok--danger" : ""}" data-role="confirm">${isDanger ? "Delete" : "Confirm"}</button>
        </div>
      </div>
    `

    const cleanup = (result) => {
      overlay.remove()
      document.removeEventListener("keydown", keyHandler)
      resolve(result)
    }

    const keyHandler = (e) => {
      if (e.key === "Escape") cleanup(false)
      if (e.key === "Enter") cleanup(true)
    }

    overlay.querySelector('[data-role="cancel"]').addEventListener("click", () => cleanup(false))
    overlay.querySelector('[data-role="confirm"]').addEventListener("click", () => cleanup(true))
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(false) })
    document.addEventListener("keydown", keyHandler)

    document.body.appendChild(overlay)
    overlay.querySelector('[data-role="confirm"]').focus()
  })
}

function escapeHtmlGlobal(text) {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}
